import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import "yup-phone";
import {
  TextField,
  Button,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import Notiflix from "notiflix";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { FormattedMessage, useIntl } from "react-intl";

import { AppDispatch, RootState } from "../../redux/store";
import { logOut, updateProfile } from "../../redux/auth/authOperations";
import PasswordChangeModal from "./PasswordChangeModal/PasswordChangeModal";
import LatinTextField from "../../helpers/latinTextField";
import PhoneField from "../../helpers/PhoneField";

export default function Profile() {
  const intl = useIntl();

  const ProfileSchema = Yup.object().shape({
    firstname: Yup.string()
      .required(intl.formatMessage({ id: "signUp.firstNameRequired" }))
      .min(2, intl.formatMessage({ id: "signUp.firstNameMinLength" }))
      .max(50, intl.formatMessage({ id: "signUp.firstNameMaxLength" })),
    lastname: Yup.string()
      .required(intl.formatMessage({ id: "signUp.lastNameRequired" }))
      .min(2, intl.formatMessage({ id: "signUp.lastNameMinLength" }))
      .max(50, intl.formatMessage({ id: "signUp.lastNameMaxLength" })),
    email: Yup.string()
      .email(intl.formatMessage({ id: "signUp.invalidEmail" }))
      .required(intl.formatMessage({ id: "signUp.emailRequired" })),
    phone: Yup.string()
      .required(intl.formatMessage({ id: "signUp.phoneRequired" }))
      .test(
        "is-valid-phone-number",
        intl.formatMessage({ id: "signUp.invalidPhoneNumber" }),
        (value) => isValidPhoneNumber(value)
      ),
  });

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const [initialValues, setInitialValues] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    dataUsageAgreed: true,
    photoAgreed: false,
  });
  const [serverError, setServerError] = useState<string | null>(null);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (user) {
      setInitialValues({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        phone: user.phone || "",
        dataUsageAgreed: user.dataUsageAgreed ?? true,
        photoAgreed: user.photoAgreed ?? false,
      });
    }
  }, [user]);

  const handleSubmit = async (
    values: {
      firstname: string;
      lastname: string;
      email: string;
      phone: string;
      dataUsageAgreed: boolean;
      photoAgreed: boolean;
    },
    { setSubmitting }: any
  ) => {
    if (values.email !== user.email) {
      Notiflix.Confirm.show(
        intl.formatMessage({ id: "profile.emailChangeConfirmation" }),
        intl.formatMessage({ id: "profile.emailChangeConfirmationMessage" }),
        intl.formatMessage({ id: "profile.yes" }),
        intl.formatMessage({ id: "profile.no" }),
        async () => {
          try {
            await dispatch(
              updateProfile({ profileData: values, language: intl.locale })
            ).unwrap();
            setServerError(null);
            setSubmitting(false);
            dispatch(logOut());
          } catch (error: any) {
            setServerError(
              error.message || intl.formatMessage({ id: "profile.updateError" })
            );
            setSubmitting(false);
          }
        },
        () => {
          setSubmitting(false);
        }
      );
    } else {
      try {
        await dispatch(
          updateProfile({ profileData: values, language: intl.locale })
        ).unwrap();
        setServerError(null);
        setSubmitting(false);
      } catch (error: any) {
        setServerError(
          error.message || intl.formatMessage({ id: "profile.updateError" })
        );
        setSubmitting(false);
      }
    }
  };

  const handleChangePassword = () => {
    setIsPasswordModalOpen(true);
  };

  return (
    <Box
      sx={{
        my: 4,
        mx: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "600px",
        margin: "0 auto",
        pt: isMobile ? 5 : 0,
      }}
    >
      {serverError && (
        <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
          {serverError}
        </Alert>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={ProfileSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, setFieldValue, values }) => (
          <Form noValidate style={{ width: "100%" }}>
            <Field
              as={LatinTextField}
              margin="normal"
              required
              fullWidth
              id="firstname"
              label={intl.formatMessage({ id: "signUp.firstName.input" })}
              name="firstname"
              autoComplete="given-name"
              error={touched.firstname && !!errors.firstname}
              helperText={touched.firstname && errors.firstname}
            />
            <Field
              as={LatinTextField}
              margin="normal"
              required
              fullWidth
              id="lastname"
              label={intl.formatMessage({ id: "signUp.lastName.input" })}
              name="lastname"
              autoComplete="family-name"
              error={touched.lastname && !!errors.lastname}
              helperText={touched.lastname && errors.lastname}
            />
            <Field
              as={TextField}
              margin="normal"
              required
              fullWidth
              id="email"
              label={intl.formatMessage({ id: "signUp.email" })}
              name="email"
              autoComplete="email"
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
            />
            <Field
              name="phone"
              component={PhoneField}
              form={{
                errors,
                touched,
                isSubmitting,
                setFieldValue,
                values,
              }}
              label={intl.formatMessage({ id: "admin.dashboard.phone" })}
            />
            <FormControlLabel
              sx={{ mt: 2, display: "block" }}
              control={
                <Checkbox
                  checked={values.dataUsageAgreed}
                  onChange={(event) =>
                    setFieldValue("dataUsageAgreed", event.target.checked)
                  }
                />
              }
              label={intl.formatMessage({ id: "signUp.dataUsageAgreed" })}
            />
            <FormControlLabel
              sx={{ display: "block" }}
              control={
                <Checkbox
                  checked={values.photoAgreed}
                  onChange={(event) =>
                    setFieldValue("photoAgreed", event.target.checked)
                  }
                />
              }
              label={intl.formatMessage({ id: "signUp.photoAgreed" })}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              <FormattedMessage id="profile.updateProfile" />
            </Button>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mt: 1, mb: 2 }}
              onClick={handleChangePassword}
            >
              <FormattedMessage id="profile.changePassword" />
            </Button>

            <PasswordChangeModal
              open={isPasswordModalOpen}
              onClose={() => setIsPasswordModalOpen(false)}
            />
          </Form>
        )}
      </Formik>
    </Box>
  );
}
